/**
 * @file LegalRevisionHistory.tsx
 * @description История редакций документа (phase19.16, SB143): номер,
 * дата, кто опубликовал, у PDF — файл редакции; «Открыть» показывает текст
 * редакции (окно) или файл (новая вкладка). Постранично — «Показать ещё».
 *
 * У PDF та же история служит «историей файлов по редакциям» в блоке файла:
 * у каждой строки — имя и размер файла этой редакции.
 */

import { formatLegalFileSize, type LegalDocumentKind, type LegalRevisionListItemDto } from "@chaiberi/shared";
import { useState } from "react";

import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import { useOpenLegalFile } from "../../hooks/legal/useOpenLegalFile";
import type { LegalRevisionsState } from "../../hooks/legal/useLegalRevisions";
import { formatLegalDate } from "../../lib/legal/legal-doc-view";
import { LegalRevisionPreview } from "./LegalRevisionPreview";

interface Props {
  docId: string;
  kind: LegalDocumentKind;
  revisions: LegalRevisionsState;
  /** Номер действующей редакции — пометка «на сайте». */
  liveNumber: number | null;
}

/** Подпись строки: «Ред. 2 · 19.10.2026 · Евгений». */
function revisionLine(r: LegalRevisionListItemDto): string {
  return [`Ред. ${r.number}`, formatLegalDate(r.publishedAt), r.publishedByName].filter(Boolean).join(" · ");
}

export function LegalRevisionHistory({ docId, kind, revisions, liveNumber }: Props) {
  const openFile = useOpenLegalFile();
  const [preview, setPreview] = useState<number | null>(null);
  const { items, hasMore, loading, error } = revisions;

  if (liveNumber === null) {
    return <p className="type-caption text-on-surface-variant">Редакций пока нет — первая появится при публикации.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {error ? (
        <NoticeStrip tone="error" action={<Button variant="outline" size="sm" onClick={revisions.reload}>Повторить</Button>}>
          {error}
        </NoticeStrip>
      ) : null}
      <ul className="flex flex-col divide-y divide-[var(--border)]">
        {items.map((r) => (
          <li key={r.number} className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-1 py-1">
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="type-body text-on-surface">
                {revisionLine(r)}
                {r.number === liveNumber ? <span className="text-on-surface-variant"> · на сайте</span> : null}
              </span>
              {r.file ? (
                <span className="type-caption break-words text-on-surface-variant">
                  {r.file.name} · {formatLegalFileSize(r.file.sizeBytes)}
                </span>
              ) : null}
              {r.note ? <span className="type-caption text-on-surface-variant">{r.note}</span> : null}
            </span>
            <Button
              variant="ghost"
              className="min-h-12 shrink-0"
              aria-label={`Открыть редакцию ${r.number}`}
              onClick={() => (kind === "PDF" ? openFile(docId, r.number) : setPreview(r.number))}
            >
              Открыть
            </Button>
          </li>
        ))}
      </ul>
      {hasMore ? (
        <Button variant="outline" className="self-start" loading={loading} onClick={revisions.loadMore}>
          Показать ещё
        </Button>
      ) : null}
      {kind === "TEXT" ? <LegalRevisionPreview docId={docId} number={preview} onClose={() => setPreview(null)} /> : null}
    </div>
  );
}
